import { groupBy, orderBy, sumBy } from "es-toolkit";

// Helpers for sale lines. The printed receipt and invoice read the VAT each line
// stored when the sale was recorded; the daily report still splits it from the
// product rates with the ones below.

// An ad-hoc line carries no product, so there is no VAT rate to read for it.
// Fall back to the standard French rate -- the same 20% the old hardcoded
// `/ 1.2` assumed, now at least named.
export const DEFAULT_VAT_RATE_PERCENT_CENTS = 2000;

/** The VAT rate applying to a sale line, in hundredths of a percent. */
export const vatRatePercentCents = (saleProduct: SaleProduct) =>
	saleProduct.product?.vatRate?.ratePercentCents ??
	DEFAULT_VAT_RATE_PERCENT_CENTS;

/** 1 + a VAT rate, e.g. 1.2 for a 20% rate. */
export const vatMultiplier = (ratePercentCents: number) =>
	1 + ratePercentCents / 10000;

/** What the line was rung up at, VAT included, before any sale-wide promotion. */
export const lineAmountCents = (saleProduct: SaleProduct) =>
	saleProduct.quantity * saleProduct.amountCents;

/**
 * What a line prints as. `customLabel` is optional on the record; an ad-hoc line
 * with neither a product nor a label prints as a blank name, not "undefined".
 */
export const lineName = (saleProduct: SaleProduct) =>
	saleProduct.product?.name ?? saleProduct.customLabel ?? "";

/** A VAT rate as printed, e.g. "20%" or "5.5%". */
export const formatVatRate = (ratePercentCents: number) =>
	`${ratePercentCents / 100}%`;

export interface VatBreakdownRow {
	ratePercentCents: number;
	taxFreeAmountCents: number;
	vatAmountCents: number;
	totalAmountCents: number;
}

/**
 * The VAT a sale declared, per rate, summed from what each line stored when the
 * sale was recorded. The rows add up to the sale's total to the cent, whatever
 * the products' rates have become since.
 */
export const vatBreakdown = (saleProducts: SaleProduct[]): VatBreakdownRow[] =>
	orderBy(
		Object.entries(
			groupBy(saleProducts, (line) => line.vatRatePercentCents),
		).map(([rate, lines]) => ({
			ratePercentCents: Number(rate),
			taxFreeAmountCents: sumBy(lines, (line) => line.taxFreeAmountCents),
			vatAmountCents: sumBy(lines, (line) => line.vatAmountCents),
			totalAmountCents: sumBy(lines, (line) => line.totalAmountCents),
		})),
		["ratePercentCents"],
		["desc"],
	);
